<template>
  <BaseDialog centered contentClass="go-nogo" ref="dialog" persistent :show-actions="true">
    <template slot="title">
      <div class="go-nogo-dialog__title">
        <div>{{ changeVote ? $t('go-nogo.change-vote') : $t('go-nogo.vote') }}</div>

        <BaseButton has-icon-left is-small isSquare isGrey v-if="isManager" @click.native="openPageAdminGoNogo">
          <BaseIcon name="edit-2" />
          {{ $t('go-nogo.configure-go-nogo') }}
        </BaseButton>
      </div>
    </template>
    <template slot="content">
      <!-- aria-pressed is bound as a STRING on purpose: Vue 2 drops an attribute bound to `false`,
           which would leave an unselected button with no toggle state to announce. -->
      <div class="base-dialog__content__choices">
        <button
          type="button"
          class="square-button go"
          :class="{ selected: selectedButton === 'go', showList: isButtonWithReasons('go') }"
          :aria-pressed="String(selectedButton === 'go')"
          @click="selectButton('go')"
        >
          <span>{{ $t('go-nogo.dialog.go') }}</span>
        </button>
        <button
          type="button"
          class="square-button nogo"
          :class="{ selected: selectedButton === 'nogo', showList: isButtonWithReasons('nogo') }"
          :aria-pressed="String(selectedButton === 'nogo')"
          @click="selectButton('nogo')"
        >
          <span>{{ $t('go-nogo.dialog.no-go') }}</span>
        </button>
      </div>
      <transition name="fade-in-slow">
        <div v-if="oppVoteReasons.length" class="go-nogo-dialog__reasons">
          <BaseChip
            v-for="reason in oppVoteReasons"
            :key="reason.id"
            size="sm"
            :tone="getGoNoGoVoteTone(selectedButton)"
            :preset="isReasonSelected(reason) ? 'soft' : 'secondary'"
            :selected="isReasonSelected(reason)"
            @click="selectReason(reason)"
          >
            {{ reason.reason }}
          </BaseChip>
        </div>
      </transition>
      <transition name="fade-in-slow">
        <div v-if="selectedButton" class="go-nogo-dialog__comment">
          <p class="go-nogo-dialog__comment-label">{{ $t('go-nogo.dialog.comment-label') }}</p>
          <p class="go-nogo-dialog__comment-hint">{{ $t('go-nogo.dialog.comment-hint') }}</p>
          <FormInput
            is-textarea
            is-compact
            name="go-nogo-comment"
            :rows="4"
            :value="commentValue"
            :placeholder="$t('go-nogo.dialog.comment-placeholder')"
            @input="commentValue = $event"
          />
        </div>
      </transition>
    </template>
    <template slot="actions">
      <BaseVoteButton @click.native="cancelClick">
        {{ $t('actions.cancel') }}
      </BaseVoteButton>
      <BaseVoteButton :disabled="!selectedButton || !hasChanges" @click.native="execute">
        {{ $t('go-nogo.dialog.confirm') }}
      </BaseVoteButton>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseVoteButton from '@/components/base/BaseVoteButton';
import BaseDialog from '@/components/base/BaseDialog';
import FormInput from '@/components/form/FormInput';
import { getGoNoGoVoteTone } from '@/util/tag-tones/go-nogo-tag';
import { formatCustomVoteReasons, partitionVoteReasons, sameVoteReasons } from '@/util/go-nogo-vote-reasons';
import { SECONDARY_VOTES_LOCKED } from '@/util/go-nogo-final-decision';
import { getApiErrorCode } from '@/util/api-error';
import { mapActions, mapGetters } from 'vuex';

// The store rewrites API vote values for display (YES -> GO), so mapping back to a button needs the
// display vocabulary, not the API one that `getVoteType` produces.
const BUTTON_BY_DISPLAY_VOTE = { GO: 'go', NOGO: 'nogo', INDIFFERENT: 'indifferent' };

export default {
  name: 'GoNoGoDialog',
  components: {
    BaseButton,
    BaseChip,
    BaseIcon,
    BaseVoteButton,
    BaseDialog,
    FormInput,
  },
  props: {
    opportunityId: String,
    changeVote: Boolean,
    userVote: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      selectedButton: null,
      subItems: [],
      commentValue: '',
    };
  },
  computed: {
    ...mapGetters('goNoGo', ['tenantOpportunityVoteReasons', 'voteReasonsLoaded']),
    ...mapGetters('auth', ['isManager', 'currentUser']),
    oppVoteReasons() {
      if (!this.selectedButton) {
        return [];
      }
      return this.tenantOpportunityVoteReasons.filter((reason) => reason.voteType === this.getVoteType());
    },
    // A no-op PUT still moves `updatedAt`, which the row's "Voté le …" date reads from, so it would
    // misreport when the person actually voted. A first vote has nothing to compare against.
    hasChanges() {
      if (!this.changeVote || !this.userVote) return true;
      if (!this.selectedButton) return false;
      if (this.selectedButton !== BUTTON_BY_DISPLAY_VOTE[this.userVote.vote]) return true;
      // Presets and the comment share one array here, so compare the array we would send.
      return !sameVoteReasons(this.buildVoteReasons(), this.userVote.reasons);
    },
  },
  methods: {
    ...mapActions('goNoGo', ['castOpportunityVote', 'getOpportunityVoteReasons', 'fetchFinalDecision']),
    getGoNoGoVoteTone,
    async openDialog() {
      this.resetForm();
      // Open first so the dialog is never gated on a request, then initialise: splitting presets
      // from the free-text comment needs the tenant presets, so it has to wait for them.
      this.$refs.dialog.openModal();
      await this.ensureVoteReasons();
      if (this.userVote?.vote) {
        this.initFromUserVote(this.userVote);
      }
    },
    /**
     * The Go/Nogo page already loads the presets alongside the votes, so only fetch when nothing
     * has. Avoids a second request per page load — which also collided with the page's own, since
     * the service cancels any in-flight reasons request.
     */
    ensureVoteReasons() {
      if (this.voteReasonsLoaded) return Promise.resolve();
      return this.getOpportunityVoteReasons();
    },
    resetForm() {
      this.selectedButton = null;
      this.subItems = [];
      this.commentValue = '';
    },
    initFromUserVote(userVote) {
      this.selectedButton = BUTTON_BY_DISPLAY_VOTE[userVote.vote] || null;

      const { presetReasons, customReasons } = partitionVoteReasons(userVote, this.tenantOpportunityVoteReasons);
      this.subItems = presetReasons;
      this.commentValue = formatCustomVoteReasons(customReasons) || '';
    },
    buildVoteReasons() {
      const reasons = [...this.subItems];
      const comment = this.commentValue.trim();
      if (comment) {
        reasons.push(comment);
      }
      return reasons;
    },
    async execute() {
      if (!this.selectedButton || !this.hasChanges) {
        return;
      }

      let vote = 'NEUTRAL';
      if (this.selectedButton === 'go') {
        vote = 'YES';
      } else if (this.selectedButton === 'nogo') {
        vote = 'NO';
      }

      const builtReasons = this.buildVoteReasons();

      try {
        await this.castOpportunityVote({
          opportunityId: this.opportunityId,
          userId: this.currentUser.id,
          userVote: this.userVote,
          vote,
          reasons: builtReasons,
        });
        this.closeDialog();
      } catch (error) {
        this.handleVoteError(error);
      }
    },
    // A final decision may have been recorded while this dialog was open, which closes voting.
    handleVoteError(error) {
      this.$toast.error(error);
      if (getApiErrorCode(error) === SECONDARY_VOTES_LOCKED) {
        this.fetchFinalDecision({ opportunityId: this.opportunityId });
        this.closeDialog();
      }
    },
    cancelClick() {
      this.closeDialog();
      this.$emit('onCancel');
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.resetForm();
    },
    selectButton(button) {
      if (this.selectedButton === button) {
        this.resetForm();
        return;
      }
      this.selectedButton = button;
      this.subItems = [];
      this.commentValue = '';
    },
    selectReason(reason) {
      const index = this.subItems.indexOf(reason.reason);
      if (index === -1) {
        this.subItems.push(reason.reason);
      } else {
        this.subItems.splice(index, 1);
      }
    },
    isReasonSelected(reason) {
      return this.subItems.includes(reason.reason);
    },
    isButtonWithReasons(button) {
      return this.tenantOpportunityVoteReasons.some((reason) => reason.voteType === this.getVoteType(button));
    },
    getVoteType(button = this.selectedButton) {
      switch (button) {
        case 'go':
          return 'YES';
        case 'indifferent':
          return 'NEUTRAL';
        case 'nogo':
          return 'NO';
        default:
          return null;
      }
    },
    openPageAdminGoNogo() {
      this.$router.push({ name: 'admin-go-nogo' });
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__go-nogo {
  min-height: max-content;
  width: 700px;
  position: relative;
  border-radius: $border-radius-root;
  height: auto !important;

  .base-dialog__content {
    background: $color-white !important;
    padding: 16px !important;

    &__choices {
      justify-content: stretch;
      display: flex;
      padding: 16px;
      flex-direction: row;
      gap: 16px;

      span {
        text-transform: uppercase;
      }
    }

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0px;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }

    .square-button {
      color: grey;
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: 12px;
      white-space: nowrap;
      overflow: hidden;
      justify-content: center;
      text-overflow: ellipsis;
      font-size: $font-size-small;
      font-weight: $font-weight-bold;
      padding: 16px;
      border-radius: $border-radius-small;
      flex: 1;
      height: 120px;
      cursor: pointer;
      transition: 200ms;
      border: 2px solid transparent;
      border-radius: $border-radius-root;
      // Real <button>s for keyboard access, so the user-agent font has to be overridden.
      font-family: inherit;
      appearance: none;

      &:focus-visible {
        outline: 2px solid $color-accent-500;
        outline-offset: 2px;
      }
    }

    .square-button.selected.go {
      border-color: $color-green;
    }

    .square-button.selected.indifferent {
      border-color: $color-border;
    }

    .square-button.selected.nogo {
      border-color: $color-error;
    }

    .go {
      color: $color-green;
      background-color: $color-green-100;
    }

    .indifferent {
      color: grey;
      background-color: $color-slate-100;
    }

    .nogo {
      color: $color-error;
      background-color: $color-red-100;
    }
  }

  .close-icon__container {
    display: none;
  }

  .base-dialog__actions {
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding: 16px;
    background: $color-white !important;
  }

  .base-dialog__header {
    width: 100%;
  }
}

.go-nogo-dialog {
  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__reasons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px;
    padding: 16px 8px 0;
    max-width: 100%;
  }

  &__comment {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 8px 0;
    width: 100%;
  }

  &__comment-label {
    margin: 0;
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    color: $color-normal;
    text-align: left;
  }

  &__comment-hint {
    margin: 0;
    font-size: $font-size-small;
    color: $color-neutral;
    line-height: 1.4;
    text-align: left;
  }
}
</style>
